<template>
  <div class="mb-3 pb-3 flex items-center gap-3 border-b border-slate-400">
    <div class="w-25 flex-shrink-0 rounded shadow-md">
      <div
        class="h-7 flex items-center justify-center rounded-t transition-all duration-300"
        :class="[deleteState ? 'bg-slate-200' : 'bg-orange-200']"
      >
        <BlTextBody2
          :text="isPattern ? 'Every' : displayDate(timeslot.date, 'dddd')"
          class="transition-all duration-300"
          :class="[deleteState ? 'text-slate-700' : 'text-orange-700']"
        />
      </div>
      <div
        class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
      >
        <BlTextTitle4
          :text="displayDate(timeslot.date, isPattern ? 'ddd' : 'DD')"
          class="transition-all duration-300"
          :class="[deleteState ? 'text-slate-700' : 'text-orange-700']"
        />
        <BlTextBody2
          v-if="!isPattern"
          :text="displayDate(timeslot.date, 'MMMM')"
          class="transition-all duration-300"
          :class="[deleteState ? 'text-slate-600' : 'text-orange-600']"
        />
      </div>
    </div>
    <div
      v-if="!deleteState"
      class="w-full py-3 px-3 flex flex-col gap-3 rounded transition-all duration-300"
      :class="{ 'bg-tomato-100': unavailableTimeslot }"
    >
      <div class="flex flex-col gap-1">
        <div
          class="max-w-88 flex items-center gap-1"
          :title="timeslot.user.name"
        >
          <BlTextBody2
            text="Therapist:"
            :class="[
              unavailableTimeslot ? 'text-tomato-700' : 'text-slate-700'
            ]"
          />
          <BlTextSubtitle3
            :text="timeslot.user.name"
            class="truncate"
            :class="[
              unavailableTimeslot ? 'text-tomato-700' : 'text-slate-700'
            ]"
          />
        </div>
        <BlTextTitle4
          :text="`${timeslot.start_time_string} - ${timeslot.end_time_string}`"
          :class="[unavailableTimeslot ? 'text-tomato-700' : 'text-slate-800']"
        />
      </div>
      <div class="flex items-center gap-2">
        <div class="max-w-64 flex items-center gap-2 truncate">
          <div
            v-if="enableBranch && timeslot.room.branch"
            class="h-7 min-w-12 max-w-24 px-2 flex items-center justify-center rounded truncate"
            :style="{
              backgroundColor: timeslot.room.branch.background_color || 'unset'
            }"
            :title="timeslot.room.branch.name"
          >
            <BlTextBody2
              class="truncate"
              :text="timeslot.room.branch.name || '-'"
              :style="{
                color: timeslot.room.branch.color || 'unset'
              }"
            />
          </div>
          <div class="truncate" :title="timeslot.room.name">
            <BlTextBody2
              :text="timeslot.room.name"
              class="truncate"
              :class="{ 'text-tomato-700': unavailableTimeslot }"
            />
          </div>
        </div>
        <div
          class="w-1 h-1 flex-shrink-0 bg-slate-600 rounded"
          :class="[unavailableTimeslot ? 'bg-tomato-700' : 'bg-slate-600']"
        ></div>
        <div class="flex-shrink-0">
          <BlTextBody2
            :text="`${timeslotCount} credit(s)`"
            :class="{ 'text-tomato-700': unavailableTimeslot }"
          />
          <BlTextBody2
            v-if="timeslot.credit_transaction_services.length"
            :text="
              `of ${timeslot.credit_transaction_services[0].package_item_name}`
            "
            :class="{ 'text-tomato-700': unavailableTimeslot }"
          />
        </div>
      </div>
      <BlTextBody2
        v-if="useStartsFrom"
        :text="`Starts from ${displayDate(timeslot.date, 'DD MMM YYYY')}`"
      />
      <div v-if="isCreditItemUnavailable" class="flex">
        <div class="px-2 bg-tomato-100 rounded">
          <BlTextSubtitle3
            text="Selected credit item unavailable"
            class="text-tomato-700"
          />
        </div>
      </div>
      <div v-if="isTherapistUnavailable" class="flex">
        <div class="px-2 bg-tomato-100 rounded">
          <BlTextSubtitle3
            text="Therapist unavailable for open room"
            class="text-tomato-700"
          />
        </div>
      </div>
      <div v-if="isSelectedSlotUnavailable" class="flex">
        <div class="px-2 bg-tomato-100 rounded">
          <BlTextSubtitle3
            text="Selected slot unavailable"
            class="text-tomato-700"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!deleteState"
      class="py-3 flex flex-col items-center flex-shrink-0"
      :class="[
        useStartsFrom ? 'h-38' : 'h-30',
        useEdit ? 'justify-between' : 'justify-center'
      ]"
    >
      <BlInputButton
        type="outlined"
        icon="delete"
        dense
        :disabled="deleteDisabled"
        @clicked="deleteState = true"
      />
      <BlInputButton
        v-if="useEdit"
        type="outlined"
        icon="edit"
        dense
        @clicked="$emit('edit')"
      />
    </div>
    <div
      v-if="deleteState"
      class="w-full py-4 px-3 flex items-center justify-between rounded bg-slate-200"
    >
      <BlTextBody2 text="You have deleted this appointment." />
      <div class="flex items-center gap-2">
        <div class="blum-loader-btn"></div>
        <BlInputButton
          type="outlined"
          label="Undo deletion"
          dense
          @clicked="deleteState = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputRadio from "../../BlInputRadio.vue";

export default {
  name: "ItemTimeslot",
  components: {
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlInputText,
    BlInputRadio
  },
  props: {
    timeslot: { type: Object },
    isPattern: { type: Boolean, default: false },
    deleteDisabled: { type: Boolean, default: false },
    useEdit: { type: Boolean, default: false },
    useStartsFrom: { type: Boolean, default: false }
    // $emit('edit')
    // $emit('delete')
  },
  data() {
    return {
      deleteState: false,
      deleteTimeout: null
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    timeslotCount() {
      return this.getSlotCount(
        this.timeslot.start_time_string,
        this.timeslot.end_time_string
      );
    },
    unavailableTimeslot() {
      return this.timeslot.is_conflicted && !this.isPattern;
    },
    isCreditItemUnavailable() {
      if (!this.isPattern) return false;
      const unavailableCredit =
        (this.timeslot &&
          this.timeslot.booking_review_details &&
          this.timeslot.booking_review_details.unavailable_credit) ||
        [];

      if (unavailableCredit.length) {
        let packageItemId = 0;
        if (this.timeslot.credit_transaction_services.length) {
          packageItemId = this.timeslot.credit_transaction_services[0]
            .package_item_id;
        }
        if (unavailableCredit[0].package_item_id === packageItemId) {
          return true;
        }
      }
      return false;
    },
    currentSlotKey() {
      // don't change object key order -- checking for similarity timeslots
      const user = (this.timeslot && this.timeslot.user) || { id: 0, name: "" };
      return JSON.stringify({
        date: this.timeslot.date,
        room_id: this.timeslot.room.id,
        user_id: user.id,
        end_time: this.timeslot.end_time_string,
        room_name: this.timeslot.room.name,
        user_name: user.name,
        start_time: this.timeslot.start_time_string
      });
    },
    isTherapistUnavailable() {
      if (!this.isPattern) return false;
      const unavailableTherapist =
        (this.timeslot &&
          this.timeslot.booking_review_details &&
          this.timeslot.booking_review_details.unavailable_therapist) ||
        [];
      const unavailableTherapistKeys = unavailableTherapist.map(i =>
        JSON.stringify(i)
      );
      if (unavailableTherapistKeys.includes(this.currentSlotKey)) return true;
      return false;
    },
    isSelectedSlotUnavailable() {
      if (!this.isPattern) return false;
      const unavailableTimeslots =
        (this.timeslot &&
          this.timeslot.booking_review_details &&
          this.timeslot.booking_review_details.unavailable_timeslots) ||
        [];
      const unavailableTimeslotsKeys = unavailableTimeslots.map(i =>
        JSON.stringify(i)
      );
      if (unavailableTimeslotsKeys.includes(this.currentSlotKey)) return true;
      return false;
    }
  },
  watch: {
    deleteState(val) {
      if (!val) {
        clearTimeout(this.deleteTimeout);
      }
      if (val) {
        this.deleteTimeout = setTimeout(() => {
          this.$emit("delete", this.timeslot);
        }, 3000);
      }
    }
  },
  mounted() {},
  methods: {
    ...mapActions([""]),
    displayDate(date, format) {
      return moment(date).format(format);
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/ItemTimeslot.vue